import React, { useState } from 'react';
import {
    Box,
    Typography,
    Container,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Link } from 'react-router-dom';

const Faq = () => {
    const faqs = [
        {
            question:
                "Does the resource planning tool Insights Resource Management reside in the cloud or is it a SaaS (software as a service) tool?",
            answer:
                "Insights Resource Management is a cloud-based software (SaaS) that can be accessed anywhere via a browser."
        },
        {
            question:
                "Does the portfolio capacity planning tool model work demand, FTE supply, and actuals to identify resource gaps and bottlenecks?",
            answer: (
                <>
                    <Typography variant="body1" gutterBottom>
                        <strong>Model Work Demand –</strong> Insights Resource Management can model work demand in several ways that vary from simple to more complex. The level of complexity is determined as part of the implementation. Work demand should be modeled by role, over time.
                    </Typography>
                    <Typography variant="body1">
                        <strong>FTE Supply –</strong> Insights Resource Management has a module for managing FTE supply (i.e. capacity management). This can be tracked down to the name level. Tags can be placed on resources, such as ‘contractor’ or ‘new hire,’ so managers can see more granularity about hiring plans, when contractors roll off projects, etc.
                    </Typography>
                </>
            )
        },
        {
            question: "Does Insights Resource Management accommodate algorithm-based demand modeling?",
            answer:
                "Insights Resource Management models work demand in several ways, varying in complexity from simple to more complex algorithms. The level of complexity (specifically, in terms of the demand project algorithms) should be determined as part of the implementation. Work demand can be modeled by role, over time."
        },
        {
            question: "Does Insights Resource Management accommodate scenario modeling?",
            answer: (
                <>
                    <Typography gutterBottom>
                        Insights Resource Management has an easy-to-use scenario planning model. The user is presented with a Gantt-like view of their portfolio. The user can “grab”:
                    </Typography>
                    <ul>
                        <li>The end date of the bar for the project and move the end date</li>
                        <li>The start date of the bar for the project and move the start date</li>
                        <li>The whole bar and move the entire bar</li>
                    </ul>
                    <Typography gutterBottom>
                        With any of these changes the user can see the resource demand shift automatically as the user moves the timelines.
                    </Typography>
                    <Typography gutterBottom>
                        Users can move the summary bar of the project or expand the project and move the bars (timelines) for any subtasks. This design allows users to choose how specific they need to be when doing scenario modeling yet to be able to do it on one interface that is simple and easy-to-use.
                    </Typography>
                    <Typography>
                        Once the scenario is created it can be saved to analyze later. Several scenarios can be saved and published as a new workbook, replacing what was previously used.
                    </Typography>
                </>
            )
        },
        {
            question: "Does Insights Resource Management require minimal customization to perform essential requirements?",
            answer:
                "Yes, the concept behind a SaaS solution is that Intrinsic took over 20 years of pharmaceutical resource management experience and built a best-in-class resource management tool with all the essential features. This allows pharmaceutical companies to implement Insights Resource Management in as fast as a week."
        },
        {
            question: "Does Insights Resource Management provide the ability to visualize work demand versus supply (capacity) versus actual data?",
            answer:
                "Insights Resource Management has out-of-the-box reports that allow users to visualize work demand versus supply. Users can save reports to their profile and share reports they created (for example, filtering on a project or TA). That way a user’s saved reports can be stored in Insights Resource Management. Any or all of the data can also be exported to Excel."
        },
        {
            question: "Does Insights Resource Management model the work demand using algorithms and project plan milestones?",
            answer:
                "Insights Resource Management has an algorithm engine to model work demand, utilizing project plan milestones and activity dates. Algorithms can be as complex as users like, including simple “low, medium, high” allocations. If more complexity is needed, users can create custom mathematical expressions that can be applied to projections."
        },
        {
            question: "Once the work demand is generated, can the data be visualized and filtered based on therapeutic area, program, study, activity, and role?",
            answer:
                "Insights Resource Management has out-of-the-box reports with numerous filters, such as therapeutic area, program, study, activity, and role. In addition, users can add tags in Microsoft Project (e.g., outsourced) and import these tags into the tool to use as filters."
        },
        {
            question: "Can the demand model be adjusted through scenario planning?",
            answer:
                "The demand model in Insights Resource Management can be adjusted through scenario planning as described above."
        },
        {
            question: "After the demand is modeled, can Insights Resource Management import current supply or create a hiring plan by function and role?",
            answer:
                "Insights Resource Management allows pharmaceutical companies to import supply or headcount into the system. A hiring plan can also be created by function or role to assist managers with more clarity on their resource management plans."
        },
        {
            question: "Can all the data and charts be analyzed and exported for use outside the tool?",
            answer: (
                <>
                    <Typography gutterBottom>
                        The resource management reports can be saved within Insights Resource Management or exported from the tool.
                    </Typography>
                    <ul>
                        <li>Reports can be generated, saved and shared within the tool.</li>
                        <li>Reports can be exported to Excel or CSV for analysis outside the tool.</li>
                        <li>Visualizations can be exported as PNG or SVG files for sharing externally.</li>
                        <li>Data tables can be individually exported or exported in bulk per business unit (“Domain”).</li>
                        <li>Demand projections can be exported to CSV files.</li>
                    </ul>
                </>
            )
        },
        {
            question: "Are all data and charts exportable in Insights Resource Management?",
            answer:
                "Yes. Data and charts can be exported via individual data tables or as a complete data export file."
        },
        {
            question: "Can custom visualizations be created?",
            answer:
                "Insights Resource Management provides out-of-the-box reports and supports data export for use in visualization tools like Microsoft Power BI or Tableau."
        },
        {
            question: "Can Insights Resource Management roll up and drill down on data elements?",
            answer:
                "Insights Resource Management makes it easy to slice and dice your data. Users can tag additional data elements (e.g., outsourced, number of patients, sites) and filter or include/exclude them using Complexity Rules or Custom Algorithms. Drill-down and pivoting capabilities exist for Month, Quarter, Year, role, department, project, and activity."
        },
        {
            question: "Does Insights Resource Management have the ability to add custom criteria for analysis?",
            answer:
                "Insights Resource Management allows tagging of additional data columns (e.g., outsourced, number of patients, number of sites), which can be filtered, targeted for inclusion/exclusion, or used for custom projection calculations."
        },
        {
            question: "Does Insights Resource Management have the ability to forecast resources based on mathematical algorithms)?",
            answer:
                "Insights Resource Management models work demand in several ways, varying in complexity from simple to advanced algorithms. The level of complexity should be determined during implementation. Work demand is modeled by role, over time."
        },
        {
            question: "Does Insights Resource Management have a variety of security permissions?",
            answer:
                "Insights Resource Management supports role-based permissions by role, function, or individual. Permissions can be configured at both application and API levels for each business unit (Domain), with levels such as read, read/write, and admin access."
        },
        {
            question: "Does Insights Resource Management comply with single-sign-on?",
            answer:
                "Yes. Single-sign-on is supported via Microsoft Office 365. Once invited, the login is seamless using the same credentials as other Microsoft 365 tools."
        },
        {
            question: "Can Insights Resource Management scale beyond R&D?",
            answer:
                "Yes. Insights Resource Management uses domains—containers of data grouped by department or function. Privileges can be set so users only access their respective domains, allowing the tool to scale easily across the enterprise beyond R&D."
        }
    ];

    const [expanded, setExpanded] = useState(false);
    const handleChange = (panel) => (event, isExpanded) => setExpanded(isExpanded ? panel : false);

    return (
        <Box sx={{ background: 'linear-gradient(to bottom, #f7fafc, #e3f2f7)', py: 8 }}>
            <Container maxWidth="md">
            <Typography variant="h4" fontWeight="bold" mb={4} align="center">
                 INSIGHTS RESOURCE MANAGEMENT FAQ
            </Typography>
                <Typography
                    variant="body1"
                    align="center"
                    sx={{ mb: 5, color: '#555', lineHeight: 1.7 }}
                >
                    Intrinsic’s Insights Resource Management tool is a powerful yet easy-to-use resource
                    planning application that helps ensure your projects are properly resourced and your
                    resources are fully utilized.
                </Typography>





                {faqs.map((faq, index) => (
                    <Box key={index}>
                        <Accordion
                            expanded={expanded === index}
                            onChange={handleChange(index)}
                            sx={{
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                                borderBottom: '1px solid #ccc',
                                '&:before': { display: 'none' }
                            }}
                        >
                            <AccordionSummary
                                expandIcon={
                                    expanded === index ? (
                                        <RemoveIcon sx={{ color: '#2f4f60' }} />
                                    ) : (
                                        <ExpandMoreIcon sx={{ color: '#2f4f60' }} />
                                    )
                                }
                            >
                                <Typography sx={{ fontWeight: 600, color: '#2f4f60', fontSize: '1rem' }}>
                                    {faq.question}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography sx={{ color: '#333', lineHeight: 1.7 }}>{faq.answer}</Typography>
                            </AccordionDetails>
                        </Accordion>
                        {index < faqs.length - 1 && <Divider />}
                    </Box>
                ))}

                <div style={{ textAlign: 'center', marginTop: '3rem' }}>
                    <Button
                        variant="contained"
                        component={Link}
                        to="/request-demo"
                        sx={{
                            backgroundColor: '#03a9f4',
                            padding: '8px 32px',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: { xs: '1rem', md: '1.1rem' },
                            textTransform: 'none',
                            borderRadius: '20px',
                            '&:hover': {
                                backgroundColor: '#0288d1',
                            },
                        }}
                    >
                        Request a Demo
                    </Button>
                </div>

            </Container>
        </Box >
    );
};

export default Faq;

